import { Button, styled, useTheme } from '@mui/material';
import { Table } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo } from 'react';
import { DatePicker } from '../../../components/DatePicker/DatePicker';
import { SearchInput } from '../../../components/SearchInput/SearchInput';
import { Typography } from '../../../components/Typography/Typography';
import { SelectItem, Timesheet } from '../../../types';
import { useAtom, useAtomValue } from 'jotai';
import {
  issuedSelectedDate,
  issuedTableSearch,
  issuedSelectedOPs,
} from '../../../state/UITimesheetsState';
import { Multiselect } from '../../../components/Multiselect/Multiselect';
import { isSmallScreenState, userState } from '../../../state/UIState';

type Props = {
  table: Table<Timesheet>;
  hideDateFilter?: boolean;
  /** When set, replaces `table.getRowCount()` in the summary label. */
  filteredTimesheetCount?: number;
  /** Proxy (Issued Timesheets) view — route-driven. Controls the Submitter filter so a dual
   *  OP+Proxy user gets it only on the Proxy page, not on their own Pending Timesheets. */
  isProxyView?: boolean;
};

export function FilterSection({
  table,
  hideDateFilter,
  filteredTimesheetCount,
  isProxyView = false,
}: Props) {
  const { colors } = useTheme();
  const isSmallScreen = useAtomValue(isSmallScreenState);

  const [dateFilterValue, setDateFilterValue] = useAtom(issuedSelectedDate);
  const [searchValue, setSearchValue] = useAtom(issuedTableSearch);
  const [selectedOPs, setSelectedOPs] = useAtom(issuedSelectedOPs);
  const user = useAtomValue(userState);
  const isProxy = isProxyView;

  const onChangeDateFilter = useCallback(
    (date: Date | null) => {
      //add two hours to date to match the date format
      date?.setHours(date.getHours() + 2);
      const filterValue = date ? date.toISOString() : null;
      setDateFilterValue(filterValue);
    },
    [setDateFilterValue]
  );

  const onChangeOPFilter = useCallback(
    (OPs: SelectItem[] | undefined) => {
      if (OPs) {
        setSelectedOPs(OPs);
        table.getColumn('user')?.setFilterValue(user?.id ?? '');
      }
    },
    [setSelectedOPs, table, user?.id]
  );

  const isResetDisabled = useMemo(() => {
    const hasSearchValue = searchValue && searchValue.length > 0;
    const hasDateFilter = dateFilterValue !== null;
    const hasOPFilter = selectedOPs && selectedOPs.length > 0;

    return !hasSearchValue && !hasDateFilter && !hasOPFilter;
  }, [searchValue, dateFilterValue, selectedOPs]);

  const sortedOPList = useMemo(
    () => {
      if (!isProxyView) return [];
      const allOPs = Array.from(table.getColumn('user')?.getFacetedUniqueValues().keys() ?? [])
        .sort()
        .map((user) => ({ id: user.id, value: user.name }));

      const uniqueOPs = Array.from(new Map(allOPs.map((item) => [item.id, item])).values());
      return uniqueOPs;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table]
  );

  const onFiltersReset = useCallback(() => {
    setSearchValue('');
    setDateFilterValue(null);
    setSelectedOPs([]);
  }, [setDateFilterValue, setSearchValue, setSelectedOPs, table]);

  useEffect(() => {
    table.getColumn('timesheetRows')?.setFilterValue(searchValue);
  }, [searchValue, table]);

  useEffect(() => {
    if (hideDateFilter) {
      table.getColumn('weekStart')?.setFilterValue(undefined);
      return;
    }
    table.getColumn('weekStart')?.setFilterValue(dateFilterValue);
  }, [dateFilterValue, table, hideDateFilter]);

  useEffect(() => {
    if (!isProxyView) return;
    table.getColumn('user')?.setFilterValue(selectedOPs);
  }, [isProxyView, selectedOPs, table]);

  return (
    <Container>
      <SearchInput
        placeholder={isSmallScreen ? 'Deals' : 'Search deals'}
        style={{ width: isSmallScreen ? '150px' : '300px', height: '36px' }}
        onClear={() => setSearchValue('')}
        onChange={(e) => setSearchValue(e.target.value)}
        value={searchValue ?? ''}
      />
      {isProxy && (
        <Multiselect
          style={{ width: isSmallScreen ? '150px' : '200px', marginRight: '14px' }}
          fieldPlaceholder={isSmallScreen ? 'Submitter' : 'Select submitter'}
          options={sortedOPList as SelectItem[]}
          value={selectedOPs}
          onChange={(_, OPs) => onChangeOPFilter(OPs)}
        />
      )}
      {!hideDateFilter && (
        <DatePicker
          clearable
          selectedDate={dateFilterValue}
          setSelectedDate={onChangeDateFilter}
          disableFuture
          views={['year', 'month']}
          inputFormat='MM/yyyy'
          placeholder={isSmallScreen ? 'Month and year' : 'Select month and year'}
        />
      )}
      <Typography variant='caption' color={colors.text.secondary}>
        {`${
          filteredTimesheetCount !== undefined ? filteredTimesheetCount : table.getRowCount()
        } timesheets`}
      </Typography>
      <Button
        onClick={onFiltersReset}
        variant='text'
        style={{ height: '28px', marginTop: '2px', marginLeft: isSmallScreen ? 'auto' : '0px' }}
        disabled={isResetDisabled}
        sx={{
          '.MuiButton-startIcon': { marginRight: '2px' },
          '&.Mui-disabled': { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'auto' },
        }}
      >
        <Typography variant='subtitle1' color={colors.textAccent.default}>
          Reset Filters
        </Typography>
      </Button>
    </Container>
  );
}

const Container = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;
